import React, { useState } from "react";
import { toast } from "./ui/sonner";
import { Button } from "./ui/button";
import { FileSignature, FileText, Loader2, Send } from "lucide-react";
import { openAuthedPdf } from "../lib/pdf";

const fmt = (n) => (n == null ? "-" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

/* 价格获批后：预览系统生成的分包合同 PDF → 选择签署链接有效期 → 明确点击「发送合同」（发送后归档到项目文件 + 分包商库合同档案） */
export const RfqContractPanel = ({ api, rfq, k, zh, canManage, onChanged }) => {
  const [days, setDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const sel = rfq.invites?.find((i) => i.id === rfq.selectedInviteId);
  const previewPdf = () => openAuthedPdf(api, `/rfq/${rfq.id}/contract-preview.pdf`, `subcontract-${rfq.trade}.pdf`).catch((e) => toast.error(e?.response?.data?.detail || e.message));
  const send = async () => {
    if (!window.confirm(zh ? `确认向 ${sel?.companyName || "分包商"} 发送分包合同并发起在线签署（链接 ${days} 天内有效）？` : `Send the subcontract to ${sel?.companyName} for e-signature (${days}-day link)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/rfq/${rfq.id}/send-contract`, { days });
      const ch = data?.channels || {};
      const via = [ch.bell && (zh ? "铃铛" : "portal"), ch.sms && (zh ? "短信" : "SMS"), ch.email && (zh ? "邮件" : "email")].filter(Boolean).join(" / ");
      // 需求（2026-10-24，老板）：公司要求全局短信/邮件总开关保持关闭，发送失败不得再伪装成
      // 成功——之前三渠道全失败也只弹绿色「已发送（仅链接）」，分包商收不到十天没人发现。
      // 现在任何渠道没送达都改弹警告，并透出后端给出的原因（开关关闭 / 凭证失效 /
      // 未登记联系方式 / 未绑定账号），引导复制链接手动发送或联系管理员。
      // 注意：双签改造后 send-contract 走 SUB_SIGNING.start、响应里没有 channels——通知
      // 移到「生成签署链接」那一步（面板会逐渠道显示原因），这里没有回执就不能臆报失败。
      const hasChannels = "bell" in ch || "sms" in ch || "email" in ch;
      const missed = [
        !ch.bell && (ch.bellInfo || (zh ? "站内通知未发出" : "portal notice not sent")),
        !ch.sms && (ch.smsInfo || (zh ? "短信未发出" : "SMS not sent")),
        !ch.email && (ch.emailInfo || (zh ? "邮件未发出" : "email not sent")),
      ].filter(Boolean);
      if (hasChannels && missed.length) {
        toast.warning(zh ? `合同链接已生成并归档，但通知没有全部送达：${missed.join("；")}。可复制签署链接手动发给分包商。` : `Contract link created and archived, but some notices were not delivered: ${missed.join("; ")}. Copy the signing link to send it manually.`);
      } else {
        toast.success(zh ? `合同已发送（${via || "仅链接"}），已归档到项目文件${data?.archive?.archivedToCompany ? " + 分包商库合同档案" : ""}` : `Contract sent (${via || "link only"})`);
      }
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发送失败" : "Failed")); }
    setBusy(false);
  };
  if (rfq.status === "contracted") {
    return (
      <div data-testid={`rfq-contracted-${k}`} className="rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 space-y-1">
        <p className="font-semibold text-emerald-900 inline-flex items-center gap-1"><FileSignature className="h-3.5 w-3.5" />{zh ? `合同已发送签署 · ${sel?.companyName || ""} ${fmt(rfq.agreed?.amount)}` : `Contract sent · ${sel?.companyName || ""} ${fmt(rfq.agreed?.amount)}`}</p>
        <button data-testid={`rfq-contract-pdf-${k}`} onClick={previewPdf} className="inline-flex items-center gap-1 text-teal-800 underline"><FileText className="h-3 w-3" />{zh ? "查看合同 PDF" : "View contract PDF"}</button>
      </div>
    );
  }
  return (
    <div data-testid={`rfq-contract-panel-${k}`} className="rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 space-y-1.5">
      <p className="font-semibold text-emerald-900 inline-flex items-center gap-1"><FileSignature className="h-3.5 w-3.5" />{zh ? "价格已批准 · 合同草稿已生成，请先预览再发送" : "Price approved · contract draft ready — preview, then send"}</p>
      <p className="text-slate-700">{sel?.companyName} · {zh ? "成交价" : "Agreed"} <b>{fmt(rfq.agreed?.amount)}</b>{rfq.agreed?.startDate ? ` · ${zh ? "开工" : "start"} ${rfq.agreed.startDate}` : ""} · {zh ? "合同" : "contract"} {rfq.subcontractId}<span className="text-slate-400 ml-1">· {rfq.review?.path === "pm_direct" ? (zh ? "预算内直批" : "within budget") : (zh ? `${rfq.review?.decidedBy || ""} 审批通过` : `approved by ${rfq.review?.decidedBy || ""}`)}</span></p>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" data-testid={`rfq-contract-preview-${k}`} onClick={previewPdf} className="h-9 text-xs border-emerald-400 text-emerald-800"><FileText className="h-3 w-3 mr-1" />{zh ? "预览合同 PDF" : "Preview contract PDF"}</Button>
        {canManage && (
          <>
            <label className="inline-flex items-center gap-1 text-slate-600">{zh ? "签署链接有效期" : "Link valid"}
              <select data-testid={`rfq-contract-days-${k}`} value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded border border-slate-300 px-1 bg-white">{[7, 14, 30].map((d) => <option key={d} value={d}>{d} {zh ? "天" : "days"}</option>)}</select>
            </label>
            <Button size="sm" data-testid={`rfq-contract-send-${k}`} disabled={busy} onClick={send} className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 ml-auto">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}{zh ? "发送合同（发起在线签署）" : "Send contract"}</Button>
          </>
        )}
      </div>
    </div>
  );
};
